import { Component, DestroyRef, EventEmitter, Input, Output } from '@angular/core';
import { outputToObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SafeHtml } from '@angular/platform-browser';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { StorageService } from 'src/app/services/storage.service';

@Component({
    selector: 'app-progress-modal',
    templateUrl: './progress-modal.component.html',
    styleUrls: ['./progress-modal.component.scss'],
    standalone: false
})
export class ProgressModalComponent {
  @Input() progressData: IProgressData;
  @Input() showEstimation: boolean = false;
  @Input() modalRef!: ModalDirective;
  @Output() closeModal = new EventEmitter();
  @Output() primaryAction = new EventEmitter();
  @Output() secondaryAction = new EventEmitter();

  isRegulated: boolean = false;

  minutes: string = '00';
  seconds: string = '00';
  milliseconds: string = '00';
  countdownEnded: boolean = false;

  private totalDurationInS!: number;
  private totalDurationInMs!: number;
  private endTime!: number;
  private intervalId!: NodeJS.Timeout;

  imageLoading: boolean = true;

  constructor(private webStorageService: StorageService, private destroyRef: DestroyRef) {}

  ngOnInit(): void {
    this.isRegulated = this.webStorageService.getItemSession('isRegulated');
  }

  ngAfterViewInit(): void {
    if (!this.modalRef) return;
    // ngx-bootstrap 21 exposes these as Angular outputs, not RxJS subjects.
    //Listens to this modal's onShown Event
    outputToObservable(this.modalRef.onShown).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.showEstimation) this.startCountDown(90);
    });
    outputToObservable(this.modalRef.onHidden).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.resetTimer();
      this.countdownEnded = false;
    });
  }

  /**
   * Starts the countdown timer with a total duration in seconds.
   * The method calculates the end time based on the current time and the total duration.
   * It then sets up an interval that updates the remaining time once per second,
   * formatting the time into minutes and seconds.
   * If the countdown reaches zero, the timer stops.
   *
   * @param {number} totalDurationInS
   *
   */
  private startCountDown(totalDurationInS: number) {
    this.totalDurationInS = totalDurationInS;
    this.totalDurationInMs = this.totalDurationInS * 1000;
    this.endTime = Date.now() + this.totalDurationInMs;
    this.intervalId = setInterval(() => {
      const remainingDuration: number = this.endTime - Date.now();

      if (remainingDuration > 0) {
        this.countdownEnded = false;
        const minutes = Math.floor((remainingDuration % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((remainingDuration % (1000 * 60)) / 1000);

        this.minutes = String(minutes).padStart(2, '0');
        this.seconds = String(seconds).padStart(2, '0');
      } else {
        this.resetTimer();
        this.countdownEnded = true;
      }
    }, 1000);
  }

  /**
   * Resets the countdown timer to its initial state.
   * The method sets the minutes, seconds, and milliseconds back to '00',
   * clears the interval that was set to update the countdown,
   * and marks the countdown as ended.
   *
   */
  private resetTimer() {
    this.minutes = '00';
    this.seconds = '00';
    this.milliseconds = '00';
    clearInterval(this.intervalId);
  }
  
  /**
   * Getter for `ProgressStepStatus`.
   * 
   * @returns {typeof ProgressStepStatus} - The `ProgressStepStatus` enum or object used for step status management.
   */
  get _stepStatus():typeof ProgressStepStatus{
    return ProgressStepStatus
  }

  get hasSteps(): boolean {
    return !!this.progressData?.steps?.length;
  }

  get showSteps(): boolean {
    return this.hasSteps && !(this.isSuccessState && this.progressData?.hideStepsOnSuccess);
  }

  get isSuccessState(): boolean {
    const lastStep = this.progressData?.steps?.[this.progressData?.steps?.length - 1];
    return !this.isFailureState && !!this.progressData?.steps?.length && this.progressData?.currentStep === this.progressData?.steps?.length && lastStep?.status === ProgressStepStatus.COMPLETED;
  }

  get isFailureState(): boolean {
    return !!this.progressData?.failed || this.progressData?.steps?.some((step) => step.status === ProgressStepStatus.FAILED);
  }

  get isPendingState(): boolean {
    return !this.isSuccessState && !this.isFailureState;
  }

  get canClose(): boolean {
    return this.isSuccessState || this.isFailureState || !!this.progressData?.showButton;
  }

  get headerTitle(): string {
    if (this.isSuccessState) {
      return this.progressData?.successTitle || 'Completed successfully.';
    }

    if (this.isFailureState) {
      return this.progressData?.failureTitle || 'Transaction failed.';
    }

    return this.progressData?.title || 'Processing.';
  }

  get headerMessage(): string | SafeHtml {
    if (this.isSuccessState) {
      return this.progressData?.successMessage || '';
    }

    if (this.isFailureState) {
      return this.progressData?.failureMessage || '';
    }

    return this.progressData?.pendingMessage || '';
  }

  get footerText(): string {
    if (this.isSuccessState) {
      return this.progressData?.successFooterMessage ?? this.progressData?.footerMessage ?? '';
    }

    if (this.isFailureState) {
      return this.progressData?.failureFooterMessage ?? this.progressData?.footerMessage ?? '';
    }

    if (this.isRegulated && this.showEstimation) {
      const timerText = `${this.minutes}:${this.seconds}`;
      return this.countdownEnded ? 'This is taking longer than usual, please wait.' : timerText;
    }

    return this.progressData?.footerMessage ?? '';
  }

  get isFooterTimer(): boolean {
    return this.isPendingState && this.isRegulated && this.showEstimation;
  }

  ngOnDestroy(): void {
    this.resetTimer();
  }
}


export interface IProgressData {
  title: string;
  currentStep: number;
  steps: IProgressStep[];
  failed: boolean;
  showButton:boolean;
  successTitle?:string;
  successTitleHtml?: SafeHtml;
  successMessage?:string | SafeHtml;
  failureTitle?: string;
  failureMessage?: string;
  pendingMessage?: string;
  footerMessage?: string;
  successFooterMessage?: string;
  failureFooterMessage?: string;
  details?: IProgressDetail[];
  links?: IProgressLink[];
  primaryActionLabel?: string;
  secondaryActionLabel?: string;
  hideDefaultCloseButton?: boolean;
  hideStepsOnSuccess?: boolean;
}

export interface IProgressStep {
  title: string;
  status: ProgressStepStatus;
  description?: string;
}

export interface IProgressDetail {
  label: string;
  value: string | SafeHtml;
  isHtml?: boolean;
}

export interface IProgressLink {
  label: string;
  href: string;
  external?: boolean;
}

export enum ProgressStepStatus {
  NOT_INITIATTED = 0,
  IN_PROGRESS = 1,
  COMPLETED = 2,
  FAILED = 3
}
